import { subscribeUserUiPreferences } from "../lib/userUiPreferencesSync.js";
import React from "react";
import { createPortal } from "react-dom";
import { CalendarDays, FileText } from "lucide-react";
import {
  ActionButton,
  joinClasses,
} from "./ResultsPrimitives.jsx";
import DataManagerOrdersPdfDialog from "./DataManagerOrdersPdfDialog.jsx";
import ReportEmailActions from "./ReportEmailActions.jsx";
import ReportsDisabledActionHoverCard from "./ReportsDisabledActionHoverCard.jsx";
import { LoadingClientAutocompleteInput } from "./DataManagerOrdersPrimitives.jsx";
import {
  downloadReportsOrdersPdf,
  isExportApiError,
  markExportDocumentWindowFailed,
  openExportDocumentWindow,
} from "../lib/exportApi.js";
import {
  fetchColumnPreferences,
  fetchDataManagerState,
  saveColumnPreferences,
} from "../lib/dataManagerApi.js";
import { matchOrderAdvancedFilters } from "../utils/dataManagerOrderSearch.js";
import {
  ORDER_PDF_COLUMN_CATALOG,
  ORDER_PDF_DEFAULT_COLUMN_KEYS,
  ORDER_PDF_PREFERENCE_KEY,
  buildOrderPdfExportColumns,
  buildPersistedOrderPdfColumnLabels,
  normalizeOrderPdfColumnLabels,
  resolveOrderPdfColumnKeys,
  resolveOrderPdfColumnLabels,
} from "../utils/dataManagerOrdersCustomization.js";
import {
  buildDataManagerOrdersExportViewModel,
  buildOrdersPeriodActiveFilters,
  buildOrdersPeriodScopeLabel,
  shouldResyncOrdersReportDateRange,
} from "../utils/buildDataManagerOrdersExportViewModel.js";
import { buildLoadingClientCorrespondenceIndex } from "../hooks/useDataManagerOrdersUtils.js";
import { defaultReportEmailItem } from "../utils/reportEmailForm.js";
import { getReactPortalRoot } from "../utils/reactPortalRoot.js";

const REPORTS_ACTION_ROW_CLASS_NAME = "reports-card-body";
const REPORTS_ACTION_SLOT_CLASS_NAME = "reports-card-actions";
const REPORTS_PRIMARY_ACTION_CLASS_NAME = "reports-primary-action";
const REPORTS_EMAIL_ACTIONS_CLASS_NAME = "reports-email-actions";
const DATE_INPUT_CLASS_NAME = "min-h-10 min-w-0 w-full rounded-[4px] border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 outline-none transition focus:border-border-accent-muted focus:ring-2 focus:ring-border-accent-subtle disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400";
const FILTER_PASTILLE_CLASS_NAME = "reports-field";

function normalizeDateInput(value) {
  return typeof value === "string" ? value.trim() : "";
}

function normalizeInlineText(value) {
  return typeof value === "string" ? value.trim() : "";
}

function resolveFeedbackToneClassName(tone) {
  if (tone === "danger") {
    return "border border-[var(--color-border-danger-subtle)] bg-danger-soft text-danger";
  }

  if (tone === "success") {
    return "border border-[var(--color-border-badge-subtle)] bg-[var(--grain-badge-bg)] text-[var(--grain-badge-text)]";
  }

  return "border border-line-soft bg-white text-muted-strong";
}

function sortOrdersForReportsPdf(orders) {
  return [...orders].sort((left, right) => {
    const dateCmp = String(left?.delivery_date || "").localeCompare(String(right?.delivery_date || ""));
    if (dateCmp !== 0) {
      return dateCmp;
    }
    return String(left?.order_id || "").localeCompare(String(right?.order_id || ""), "fr-CA", { sensitivity: "base" });
  });
}

export default function ReportsOrdersPdfCard({
  dispatchDate = "",
  onExported = null,
}) {
  const normalizedDispatchDate = normalizeDateInput(dispatchDate);
  const [dateFrom, setDateFrom] = React.useState(normalizedDispatchDate);
  const [dateTo, setDateTo] = React.useState(normalizedDispatchDate);
  const [clientFilter, setClientFilter] = React.useState("");
  const [clientCorrespondences, setClientCorrespondences] = React.useState([]);
  const [exportOrders, setExportOrders] = React.useState([]);
  const [isLoadingOrders, setIsLoadingOrders] = React.useState(false);
  const [isPdfDialogOpen, setIsPdfDialogOpen] = React.useState(false);
  const [isPdfExporting, setIsPdfExporting] = React.useState(false);
  const [feedback, setFeedback] = React.useState({ message: "", tone: "neutral" });
  const [committedPdfColumnKeys, setCommittedPdfColumnKeys] = React.useState(() => resolveOrderPdfColumnKeys(null));
  const [committedPdfColumnLabels, setCommittedPdfColumnLabels] = React.useState(() => resolveOrderPdfColumnLabels(null));
  const [pdfSelectedColumnKeys, setPdfSelectedColumnKeys] = React.useState(() => resolveOrderPdfColumnKeys(null));
  const [pdfColumnLabels, setPdfColumnLabels] = React.useState(() => resolveOrderPdfColumnLabels(null));
  const previousDispatchDateRef = React.useRef(normalizedDispatchDate);
  const dateRangeRef = React.useRef({ from: normalizedDispatchDate, to: normalizedDispatchDate });
  const confirmedPdfPreferencesRef = React.useRef({
    columnKeys: [...committedPdfColumnKeys],
    columnLabels: { ...committedPdfColumnLabels },
  });
  const hasLivePdfPreferences = React.useRef(false);
  React.useEffect(() => subscribeUserUiPreferences(`columns:${ORDER_PDF_PREFERENCE_KEY}`, (preferences) => {
    if (isPdfDialogOpen || isPdfExporting) return false;
    const keys = resolveOrderPdfColumnKeys(preferences);
    const labels = resolveOrderPdfColumnLabels(preferences);
    hasLivePdfPreferences.current = true;
    confirmedPdfPreferencesRef.current = { columnKeys: [...keys], columnLabels: { ...labels } };
    setCommittedPdfColumnKeys(keys); setCommittedPdfColumnLabels(labels);
    setPdfSelectedColumnKeys(keys); setPdfColumnLabels(labels);
    return true;
  }), [isPdfDialogOpen, isPdfExporting]);
  const pdfPreferenceSaveChainRef = React.useRef(Promise.resolve());
  const pdfPreferenceSaveRevisionRef = React.useRef(0);

  React.useEffect(() => {
    dateRangeRef.current = { from: dateFrom, to: dateTo };
  }, [dateFrom, dateTo]);

  React.useEffect(() => {
    const previousDispatchDate = previousDispatchDateRef.current;
    const nextDispatchDate = normalizeDateInput(dispatchDate);
    previousDispatchDateRef.current = nextDispatchDate;
    if (!nextDispatchDate || nextDispatchDate === previousDispatchDate) {
      return;
    }

    const { from, to } = dateRangeRef.current;
    if (shouldResyncOrdersReportDateRange({
      previousDispatchDate,
      dateFrom: from,
      dateTo: to,
    })) {
      setDateFrom(nextDispatchDate);
      setDateTo(nextDispatchDate);
    }
  }, [dispatchDate]);

  React.useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const payload = await fetchDataManagerState({ sections: ["client_correspondences"] });
        if (cancelled) {
          return;
        }
        setClientCorrespondences(Array.isArray(payload?.client_correspondences) ? payload.client_correspondences : []);
      } catch {
        if (!cancelled) {
          setClientCorrespondences([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const preferences = await fetchColumnPreferences(ORDER_PDF_PREFERENCE_KEY);
        if (cancelled || hasLivePdfPreferences.current) {
          return;
        }
        const keys = resolveOrderPdfColumnKeys(preferences);
        const labels = resolveOrderPdfColumnLabels(preferences);
        confirmedPdfPreferencesRef.current = {
          columnKeys: [...keys],
          columnLabels: { ...labels },
        };
        setCommittedPdfColumnKeys(keys);
        setCommittedPdfColumnLabels(labels);
        setPdfSelectedColumnKeys(keys);
        setPdfColumnLabels(labels);
      } catch {
        // Conserver le preset PDF par défaut.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadingClientOptions = React.useMemo(
    () => buildLoadingClientCorrespondenceIndex(clientCorrespondences).options,
    [clientCorrespondences],
  );
  const scopeLabel = React.useMemo(
    () => buildOrdersPeriodScopeLabel({ dateFrom, dateTo }),
    [dateFrom, dateTo],
  );
  const resultSummaryLabel = React.useMemo(() => {
    const clientName = normalizeInlineText(clientFilter);
    return [scopeLabel, clientName].filter(Boolean).join(" · ");
  }, [clientFilter, scopeLabel]);
  const normalizedDateFrom = normalizeDateInput(dateFrom);
  const normalizedDateTo = normalizeDateInput(dateTo);
  const reportEmailDate = normalizedDateFrom || normalizedDispatchDate || null;
  const reportEmailDateTo = normalizedDateTo || reportEmailDate;
  const canGenerate = Boolean(normalizedDateFrom && normalizedDateTo)
    && normalizedDateFrom <= normalizedDateTo
    && !isLoadingOrders
    && !isPdfExporting;
  const generateDisabledReason = isLoadingOrders || isPdfExporting
    ? ""
    : !normalizedDateFrom || !normalizedDateTo
      ? "Sélectionnez une date de début et une date de fin."
      : normalizedDateFrom > normalizedDateTo
        ? "La date de début doit précéder la date de fin."
        : "";

  function handleTogglePdfColumn(columnKey, isSelected) {
    setPdfSelectedColumnKeys((currentKeys) => {
      const currentSet = new Set(currentKeys);
      if (isSelected) {
        currentSet.add(columnKey);
      } else {
        currentSet.delete(columnKey);
      }
      return ORDER_PDF_COLUMN_CATALOG
        .map((column) => column.key)
        .filter((key) => currentSet.has(key));
    });
  }

  function handlePdfColumnLabelChange(columnKey, nextValue) {
    setPdfColumnLabels((currentLabels) => ({
      ...currentLabels,
      [columnKey]: nextValue,
    }));
  }

  function handleResetPdfDialog() {
    setPdfSelectedColumnKeys([...ORDER_PDF_DEFAULT_COLUMN_KEYS]);
    setPdfColumnLabels(resolveOrderPdfColumnLabels(null));
  }

  function handleClosePdfDialog() {
    if (!isPdfExporting) {
      setIsPdfDialogOpen(false);
    }
  }

  const handleGenerate = React.useCallback(async () => {
    const from = normalizeDateInput(dateFrom);
    const to = normalizeDateInput(dateTo);
    if (!from || !to) {
      setFeedback({
        message: "Sélectionnez une date de début et une date de fin.",
        tone: "danger",
      });
      return;
    }
    if (from > to) {
      setFeedback({
        message: "La date de début doit précéder la date de fin.",
        tone: "danger",
      });
      return;
    }

    setFeedback({ message: "", tone: "neutral" });
    setIsLoadingOrders(true);
    try {
      const payload = await fetchDataManagerState({
        orderDeliveryDateFrom: from,
        orderDeliveryDateTo: to,
        orderScope: "global",
        sections: ["orders"],
      });
      const orders = Array.isArray(payload?.orders) ? payload.orders : [];
      const clientName = normalizeInlineText(clientFilter);
      const filteredOrders = clientName
        ? orders.filter((order) => matchOrderAdvancedFilters(order, { client_name: clientName }))
        : orders;
      if (filteredOrders.length === 0) {
        setExportOrders([]);
        setFeedback({
          message: clientName
            ? "Aucune commande ne correspond à cette période et à ce client."
            : "Aucune commande ne correspond à cette période.",
          tone: "danger",
        });
        return;
      }

      setExportOrders(sortOrdersForReportsPdf(filteredOrders));
      setPdfSelectedColumnKeys(committedPdfColumnKeys);
      setPdfColumnLabels(committedPdfColumnLabels);
      setIsPdfDialogOpen(true);
    } catch (error) {
      setFeedback({
        message: error instanceof Error && error.message
          ? error.message
          : "Impossible de charger les commandes pour cette période.",
        tone: "danger",
      });
    } finally {
      setIsLoadingOrders(false);
    }
  }, [clientFilter, committedPdfColumnKeys, committedPdfColumnLabels, dateFrom, dateTo]);

  function handleConfirmExportPdf() {
    const normalizedPdfLabels = normalizeOrderPdfColumnLabels(pdfColumnLabels);
    const exportColumns = buildOrderPdfExportColumns(pdfSelectedColumnKeys, normalizedPdfLabels);
    const exportViewModel = buildDataManagerOrdersExportViewModel({
      activeFilters: buildOrdersPeriodActiveFilters({
        clientName: clientFilter,
        dateFrom,
        dateTo,
      }),
      exportColumns,
      rows: exportOrders,
      scopeLabel,
      sortState: { direction: "asc", key: "delivery_date" },
    });

    if (exportViewModel.resultCount === 0) {
      setFeedback({
        message: "Aucune commande à exporter dans la sélection courante.",
        tone: "danger",
      });
      return;
    }

    setIsPdfExporting(true);
    void (async () => {
      let documentWindow = null;
      try {
        documentWindow = openExportDocumentWindow({
          detail: `${exportViewModel.resultCount} commande(s) · ${exportViewModel.scopeLabel}`,
          eyebrow: "Rapports",
          message: "Nous préparons le PDF des commandes pour la période sélectionnée.",
          preferredWidth: 1240,
          title: "Préparation du PDF Commandes",
          windowName: "grain-reports-orders-pdf",
        });

        await downloadReportsOrdersPdf({
          documentWindow,
          exportViewModel,
        });

        setCommittedPdfColumnKeys(pdfSelectedColumnKeys);
        setCommittedPdfColumnLabels(normalizedPdfLabels);
        setIsPdfDialogOpen(false);

        const saveRevision = ++pdfPreferenceSaveRevisionRef.current;
        const saveOperation = pdfPreferenceSaveChainRef.current
          .catch(() => undefined)
          .then(() => saveColumnPreferences(ORDER_PDF_PREFERENCE_KEY, {
            columnLabels: buildPersistedOrderPdfColumnLabels(normalizedPdfLabels),
            columnOrder: pdfSelectedColumnKeys,
            columnWidths: {},
          }));
        pdfPreferenceSaveChainRef.current = saveOperation;
        void saveOperation.then((preferences) => {
          const confirmedColumnKeys = resolveOrderPdfColumnKeys(preferences);
          const confirmedColumnLabels = resolveOrderPdfColumnLabels(preferences);
          confirmedPdfPreferencesRef.current = {
            columnKeys: [...confirmedColumnKeys],
            columnLabels: { ...confirmedColumnLabels },
          };
          if (saveRevision === pdfPreferenceSaveRevisionRef.current) {
            setCommittedPdfColumnKeys(confirmedColumnKeys);
            setCommittedPdfColumnLabels(confirmedColumnLabels);
          }
        }).catch(() => {
          if (saveRevision === pdfPreferenceSaveRevisionRef.current) {
            const confirmedPreferences = confirmedPdfPreferencesRef.current;
            setCommittedPdfColumnKeys([...confirmedPreferences.columnKeys]);
            setCommittedPdfColumnLabels({ ...confirmedPreferences.columnLabels });
            setPdfSelectedColumnKeys([...confirmedPreferences.columnKeys]);
            setPdfColumnLabels({ ...confirmedPreferences.columnLabels });
            setFeedback({
              message: "Le PDF est généré; la dernière configuration de colonnes synchronisée a été restaurée.",
              tone: "danger",
            });
          }
        });

        if (typeof onExported === "function") {
          onExported();
        }
        setFeedback({
          message: `${exportViewModel.resultCount} commande(s) exportée(s) · le PDF s’ouvre dans une nouvelle fenêtre.`,
          tone: "success",
        });
      } catch (error) {
        const errorMessage = isExportApiError(error)
          ? error.message
          : error instanceof Error && error.message
            ? error.message
            : "Impossible de générer le PDF des commandes.";
        markExportDocumentWindowFailed(documentWindow, {
          detail: errorMessage,
          eyebrow: "Rapports",
          message: "Le PDF des commandes n’a pas pu être généré.",
          title: "Export Commandes interrompu",
        });
        setFeedback({ message: errorMessage, tone: "danger" });
      } finally {
        setIsPdfExporting(false);
      }
    })();
  }

  return (
    <article className="reports-card">
      <div className="reports-card-header">
        <div className="reports-card-icon">
          <FileText size={25} aria-hidden="true" />
        </div>
        <div className="grid gap-0.5">
          <h2 className="reports-card-title text-slate-950">Rapport Commandes</h2>
          <p className="text-[0.82rem] leading-[1.35] text-slate-600">
            Vos commandes sur une période indépendante du dispatch.
          </p>
        </div>
      </div>

      <div className={REPORTS_ACTION_ROW_CLASS_NAME}>
        <div className="reports-orders-filters">
          <label className={FILTER_PASTILLE_CLASS_NAME}>
            <span className="reports-label inline-flex items-center gap-2">
              <CalendarDays size={14} aria-hidden="true" />
              Date de début
            </span>
            <input
              type="date"
              value={dateFrom}
              onChange={(event) => {
                setDateFrom(event.target.value);
                setFeedback({ message: "", tone: "neutral" });
              }}
              className={DATE_INPUT_CLASS_NAME}
            />
          </label>

          <label className={FILTER_PASTILLE_CLASS_NAME}>
            <span className="reports-label inline-flex items-center gap-2">
              <CalendarDays size={14} aria-hidden="true" />
              Date de fin
            </span>
            <input
              type="date"
              value={dateTo}
              onChange={(event) => {
                setDateTo(event.target.value);
                setFeedback({ message: "", tone: "neutral" });
              }}
              className={DATE_INPUT_CLASS_NAME}
            />
          </label>

          <label className={FILTER_PASTILLE_CLASS_NAME}>
            <span className="reports-label">Client</span>
            <LoadingClientAutocompleteInput
              className={DATE_INPUT_CLASS_NAME}
              listId="reports-orders-pdf-client"
              onValueChange={(nextValue) => {
                setClientFilter(nextValue);
                setFeedback({ message: "", tone: "neutral" });
              }}
              options={loadingClientOptions}
              placeholder="Tous les clients"
              value={clientFilter}
            />
          </label>
        </div>

        <div className={REPORTS_ACTION_SLOT_CLASS_NAME}>
          <ReportsDisabledActionHoverCard
            actionLabel="Générer le PDF"
            disabledReason={generateDisabledReason}
            fitContent
          >
            <ActionButton
              tone="optimizer"
              size="sm"
              onClick={() => {
                void handleGenerate();
              }}
              disabled={!canGenerate}
              isLoading={isLoadingOrders}
              loadingLabel="Chargement..."
              className={REPORTS_PRIMARY_ACTION_CLASS_NAME}
            >
              Générer le PDF
            </ActionButton>
          </ReportsDisabledActionHoverCard>
          <ReportEmailActions
            className={REPORTS_EMAIL_ACTIONS_CLASS_NAME}
            items={[defaultReportEmailItem("report", "orders", {
              client_name: clientFilter,
              column_keys: committedPdfColumnKeys,
              column_labels: committedPdfColumnLabels,
              date_mode: reportEmailDate ? "fixed" : "today",
              date_to: reportEmailDateTo,
              fixed_date: reportEmailDate,
            })]}
          />
        </div>
      </div>

      {feedback.message ? (
        <div className={joinClasses([
          "rounded-[4px] px-3 py-2 text-[0.82rem] leading-5",
          resolveFeedbackToneClassName(feedback.tone),
        ])}>
          {feedback.message}
        </div>
      ) : null}

      {/* Keep the shared dialog fixed to the viewport, outside the report container. */}
      {isPdfDialogOpen ? createPortal(
        <div className="reports-page reports-orders-dialog">
          <DataManagerOrdersPdfDialog
            columnLabels={pdfColumnLabels}
            columns={ORDER_PDF_COLUMN_CATALOG}
            isExporting={isPdfExporting}
            onClose={handleClosePdfDialog}
            onExport={handleConfirmExportPdf}
            onLabelChange={handlePdfColumnLabelChange}
            onReset={handleResetPdfDialog}
            onToggleColumn={handleTogglePdfColumn}
            resultSummaryLabel={resultSummaryLabel}
            selectedColumnKeys={pdfSelectedColumnKeys}
            visibleOrderCount={exportOrders.length}
          />
        </div>,
        getReactPortalRoot(),
      ) : null}
    </article>
  );
}
